import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import NumberLineInterval from "../../../../components/visuals/NumberLineInterval";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";

/* ========== helpers ========== */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

// porównanie zbiorów przedziałów (1 lub 2)
function sameIntervals(parsed, expect) {
  if (!parsed || parsed.kind !== "intervals" || expect.kind !== "intervals") return false;
  const A = (parsed.intervals || []).slice().sort((p, q) => (p.left === -Infinity ? -1 : p.left - q.left));
  const B = (expect.intervals || []).slice().sort((p, q) => (p.left === -Infinity ? -1 : p.left - q.left));
  if (A.length !== B.length) return false;

  const eqN = (x, y) =>
    (x ===  Infinity && y ===  Infinity) ||
    (x === -Infinity && y === -Infinity) ||
    (Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) <= 1e-9);

  for (let i = 0; i < A.length; i++) {
    const P = A[i], Q = B[i];
    if (!(eqN(P.left, Q.left) && eqN(P.right, Q.right) && P.leftClosed === Q.leftClosed && P.rightClosed === Q.rightClosed)) {
      return false;
    }
  }
  return true;
}

function latexRawFromSpec(spec) {
  if (spec.variant === "between") {
    const { a, b, leftClosed, rightClosed } = spec;
    const lb = leftClosed ? "[" : "(";
    const rb = rightClosed ? "]" : ")";
    return `${lb}${a}, ${b}${rb}`;
  }
  if (spec.variant === "oneSided") {
    const { k, dir, closed } = spec;
    if (dir === "right") return `${closed ? "[" : "("}${k}, ∞)`;
    return `(-∞, ${k}${closed ? "]" : ")"}`;
  }
  // union
  const { a, b, leftRightClosed, rightLeftClosed } = spec; // domknięcie przy a (prawa kropka lewego) / przy b (lewa kropka prawego)
  const L = `(-∞, ${a}${leftRightClosed ? "]" : ")"}`;
  const R = `${rightLeftClosed ? "[" : "("}${b}, ∞)`;
  return `${L} ∪ ${R}`;
}

/* ========== generator ========== */
export function generateTask() {
  // równe szanse 1/3 na każdy wariant
  const variant = pick(["between", "oneSided", "union"]);

  if (variant === "between") {
    const a = randInt(-9, 7);
    const b = randInt(a + 1, 9);
    const leftClosed = Math.random() < 0.5;
    const rightClosed = Math.random() < 0.5;
    return {
      type: "GraphIntervalToSetTask",
      data: { variant, a, b, leftClosed, rightClosed },
    };
  }

  if (variant === "oneSided") {
    const k = randInt(-9, 9);
    const dir = Math.random() < 0.5 ? "left" : "right";   // (-∞, k] albo [k, ∞)
    const closed = Math.random() < 0.5;
    return {
      type: "GraphIntervalToSetTask",
      data: { variant, k, dir, closed },
    };
  }

  // union: rozłączne półproste z „dziurą”
  const a = randInt(-9, 6);
  const b = randInt(a + 2, 9);
  const leftRightClosed = Math.random() < 0.5;  // domknięcie przy a
  const rightLeftClosed = Math.random() < 0.5;  // domknięcie przy b
  return {
    type: "GraphIntervalToSetTask",
    data: { variant, a, b, leftRightClosed, rightLeftClosed },
  };
}

/* ========== render: treść (instrukcja + grafika) ========== */
export function renderTask({ data }) {
  return (
    <div>
      <div style={{ fontSize: 16, lineHeight: 1.5, marginBottom: 10 }}>
        Zapisz przedział zaznaczony na osi.
        {/* (Uczeń ma wpisać w formie x ∈ […]) */}
      </div>

      {data.variant === "between" && (
        <div style={{ maxWidth: 620, margin: "0.25rem auto 0" }}>
          <NumberLineRanges
            lockPair
            showZero
            ranges={[{
              from: data.a, to: data.b,
              leftClosed: data.leftClosed, rightClosed: data.rightClosed,
              leftLabelLatex: `${data.a}`, rightLabelLatex: `${data.b}`,
            }]}
          />
        </div>
      )}

      {data.variant === "oneSided" && (
        <div style={{ maxWidth: 620, margin: "0.25rem auto 0" }}>
          <NumberLineInterval
            t={data.k}
            tLabelLatex={`${data.k}`}
            dir={data.dir}
            closed={data.closed}
            margin={2.5}
          />
        </div>
      )}

      {data.variant === "union" && (
        <div style={{ maxWidth: 620, margin: "0.25rem auto 0" }}>
          <NumberLineRanges
            showZero
            ranges={[
              {
                from: -Infinity, to: data.a,
                leftClosed: false, rightClosed: data.leftRightClosed,
                rightLabelLatex: `${data.a}`,
              },
              {
                from: data.b, to: Infinity,
                leftClosed: data.rightLeftClosed, rightClosed: false,
                leftLabelLatex: `${data.b}`,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

/* ========== input (etykieta x ∈ + IntervalInput) ========== */
export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginTop: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={"x\\;\\in"} />
        </div>
        <IntervalInput
          value={userAnswer}
          onChange={onChange}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 5) ∪ [7, ∞)  lub  [−3, 4)"
        />
      </div>
    </div>
  );
}

/* ========== sprawdzanie ========== */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const parsed =
    typeof userAnswer === "string" ? parseIntervalInput(userAnswer) : userAnswer.parsed;

  if (data.variant === "between") {
    const { a, b, leftClosed, rightClosed } = data;
    const expect = { kind: "intervals", intervals: [{ left: a, right: b, leftClosed, rightClosed }] };
    return sameIntervals(parsed, expect);
  }
  if (data.variant === "oneSided") {
    const { k, dir, closed } = data;
    const expect = (dir === "right")
      ? { kind: "intervals", intervals: [{ left: k, right: Infinity, leftClosed: closed, rightClosed: false }] }
      : { kind: "intervals", intervals: [{ left: -Infinity, right: k, leftClosed: false, rightClosed: closed }] };
    return sameIntervals(parsed, expect);
  }
  const { a, b, leftRightClosed, rightLeftClosed } = data;
  const expect = {
    kind: "intervals",
    intervals: [
      { left: -Infinity, right: a, leftClosed: false, rightClosed: leftRightClosed },
      { left: b, right: Infinity, leftClosed: rightLeftClosed, rightClosed: false },
    ],
  };
  return sameIntervals(parsed, expect);
}

/* ========== odpowiedzi ========== */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || (typeof userAnswer === "object" && !userAnswer.raw)) return <i>(brak odpowiedzi)</i>;
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer.raw;
  return <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />;
}

export function renderAnswer({ data }) {
  const raw = latexRawFromSpec(data);
  return <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />;
}

/* ========== wyjaśnienie: tylko Krok 1 (bez rysunku) ========== */
export function renderExplanation({ data }) {
  const raw = latexRawFromSpec(data);
  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1.</strong> Odczytujemy z osi końce i rodzaje nawiasów
        (kropka pełna = domknięty, pusta = otwarty; przy ±∞ zawsze nawias okrągły).
        Otrzymujemy:
      </p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={`x\\in\\;${intervalToLatex(raw)}`} />
      </div>
    </div>
  );
}
